import { useCallback, useRef, useState } from 'react';
import type { AdminOrderSearchCriterion, OrderSource, OrderStatus } from '@lds-efsp/shared-contracts';
import type { OrderPriority } from '../types/admin';
import { useApi, API_BASE_URL } from './useApi';

/**
 * One row of GET /api/admin/orders — the fields the route's response map
 * actually carries (apps/api-gateway/src/features/admin/orders/routes.ts).
 * The admin orders list keys every column onto these, never onto the demo
 * shape the page used to invent.
 */
export interface AdminOrderRow {
  id: string;
  orderNumber: string;
  caseNumber: string | null;
  caseTitle: string | null;
  /** The row's account — the page's client-side company-scoping filter reads it. */
  accountId: string;
  account: string;
  /** ISO timestamp — parse before formatting. */
  receivedDate: string;
  status: OrderStatus;
  orderType: string;
  accountType: 'concierge' | 'direct';
  priority: OrderPriority;
  source: OrderSource;
  // AdminDataTable's DataRow carries an index signature; this is the same
  // accommodation AdminUsersList rows make to be assignable to its data prop.
  [key: string]: unknown;
}

export interface AdminOrdersListResponse {
  data: AdminOrderRow[];
  pagination: { page: number; per_page: number; total: number };
}

interface UseAdminOrdersApiReturn {
  orders: AdminOrderRow[];
  pagination: AdminOrdersListResponse['pagination'] | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Custom hook for the admin orders list. Fetches the live rows — scoped to
 * the active search's criterion and term when one is passed, unfiltered when
 * none is — and owns the loading/error state the table's skeleton pass reads;
 * like the admin dashboard's dispatched/on-hold fetches it swallows the error
 * into state rather than rethrowing, so a failed fetch renders the page's
 * error line instead of an unhandled rejection.
 *
 * The rows it holds are always the LATEST request's answer, never an older
 * query's: a superseded request that answers late is dropped, and a failed
 * one (the route's 400 search refusal, a 5xx, a network error) clears the
 * rows and pagination it would otherwise have left standing — the previous
 * query's rows rendered as this search's result would have the view decider
 * answer `filtered` over rows this search never produced.
 *
 * The request is issued through `fetchWithAuth` rather than `get` for the one
 * thing `get` cannot carry back: the route's search refusal (400) answers
 * with a display `hint` naming the three-character minimum, and that hint is
 * this endpoint's own error detail — read here, as the eFiling reference
 * fetches read theirs, instead of widening the shared generic extractor for
 * every caller.
 */
export function useAdminOrdersApi(
  activeSearch?: { criterion: AdminOrderSearchCriterion; term: string } | null,
): UseAdminOrdersApiReturn {
  const { fetchWithAuth } = useApi();
  const [orders, setOrders] = useState<AdminOrderRow[]>([]);
  const [pagination, setPagination] = useState<AdminOrdersListResponse['pagination'] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const latestRequest = useRef(0);

  const searchQuery = activeSearch
    ? `?${new URLSearchParams({ search: activeSearch.term, search_field: activeSearch.criterion }).toString()}`
    : '';

  const refetch = useCallback(async () => {
    // Whichever request fired last owns the state; every earlier one abstains.
    const request = ++latestRequest.current;
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/orders${searchQuery}`, {
        method: 'GET',
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        const detail = (body ?? {}) as { hint?: string; message?: string; error?: string };
        throw new Error(detail.hint || detail.message || detail.error || `Request failed (${response.status})`);
      }
      const result = (await response.json()) as AdminOrdersListResponse;
      if (request !== latestRequest.current) return;
      setOrders(result.data ?? []);
      setPagination(result.pagination ?? null);
    } catch (err) {
      if (request !== latestRequest.current) return;
      // No rows survive a failed request: the previous query's rows under the
      // active search would render as that search's result. Clearing hands an
      // active search to the empty search result; the page's error line
      // carries the route's refusal hint alongside it.
      setOrders([]);
      setPagination(null);
      console.error('Failed to fetch admin orders:', err);
      setError(err instanceof Error ? err.message : 'Failed to load orders');
    } finally {
      if (request === latestRequest.current) setIsLoading(false);
    }
  }, [fetchWithAuth, searchQuery]);

  return { orders, pagination, isLoading, error, refetch };
}
